import { useState } from 'react';
import axios from 'axios';
import { Check, ChevronsUpDown, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import SiteFields, { emptySite } from './SiteFields';
import { siteLabel } from './SitesEditor';
import { mergeSiteValue, sameSiteLocation } from '../../utils/sites';
import { extractErrorMessage } from '../../utils/apiErrors';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RecordSiteFields = ({ value, onChange, customer, onCustomerUpdated, testPrefix, dark = false }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const sites = customer?.sites || [];
  const selected = sites.find((s) => sameSiteLocation(s, value));
  const canSave = ['management', 'foreman', 'field_coordinator', 'field_management'].includes(user?.role) || user?.effective_permissions?.includes('customers.edit');
  const pick = (s) => { onChange({ ...emptySite(), ...s }); setOpen(false); };
  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/customers/${customer.id}`, { sites: mergeSiteValue(sites, value) });
      onCustomerUpdated?.(res.data);
      toast.success('Site saved to customer');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save site'));
    } finally { setSaving(false); }
  };
  return (
    <div className="min-w-0 space-y-2" data-testid={`${testPrefix}-section`}>
      <Label>Site</Label>
      {customer && (
        <Popover modal open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full min-w-0 justify-between font-normal" data-testid={`${testPrefix}-select`}>
              <span className="truncate">{selected ? siteLabel(selected) : 'New / unlisted site'}</span><ChevronsUpDown size={14} className="shrink-0 ml-2" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className={`w-[--radix-popover-trigger-width] max-w-[90vw] p-0 z-[140] ${dark ? 'dark' : ''}`} data-testid={`${testPrefix}-options`}>
            <Command>
              <CommandInput placeholder="Search site name or LSD..." data-testid={`${testPrefix}-search`} />
              <CommandList>
                <CommandEmpty data-testid={`${testPrefix}-empty`}>No matching sites.</CommandEmpty>
                <CommandGroup>
                  <CommandItem value="New / unlisted site" onSelect={() => pick(emptySite())} data-testid={`${testPrefix}-new`}>New / unlisted site</CommandItem>
                  {sites.map((s) => (
                    <CommandItem key={s.id} value={`${s.id} ${siteLabel(s)}`} onSelect={() => pick(s)} data-testid={`${testPrefix}-option-${s.id}`} className="min-w-0">
                      <Check size={14} className={`mr-2 shrink-0 ${selected?.id === s.id ? '' : 'opacity-0'}`} /><span className="min-w-0 break-all">{siteLabel(s)}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
      <SiteFields value={value} onChange={onChange} testPrefix={testPrefix} dark={dark} />
      {customer && canSave && onCustomerUpdated && (
        <Button type="button" variant="outline" size="sm" onClick={save} disabled={saving || !(value.name || value.lsd || value.address)} data-testid={`${testPrefix}-save-to-customer`}>
          <Save size={14} className="mr-1.5" />{saving ? 'Saving...' : 'Save site to customer'}
        </Button>
      )}
    </div>
  );
};